import { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

/** How loud a chip is, in the same words the rest of the panel uses for a tone. */
export type TraceTone = 'neutral' | 'ok' | 'warn' | 'error' | 'info' | 'muted';

export interface ITraceChipProps {
    /** What this value is - "synclog", "poging", "stand" - set in front of it. */
    label?: string;

    /** The value itself, or nothing when the label is the whole statement. */
    children?: ReactNode;

    icon?: IconProp;

    tone?: TraceTone;

    /** The sentence behind it, on hover. Nearly every chip here has one. */
    hint?: string;

    /** Sets the value in a monospaced face, for ids and queue messages. */
    mono?: boolean;
}

/**
 * One fact, as a badge that says what it is.
 *
 * The label in front of the value is the whole point. This panel used to hang bare fragments
 * of text off the end of a line - "Failed", "3", "poging 2" - and a value with no name is a
 * value a reader has to guess at: "Failed" is the sync log's word and "3" was the number of
 * attempts, but nothing on screen said so, and the two sat next to each other in the same
 * grey.
 *
 * So a chip carries both halves and a tone, and the tone means one thing only: how bad this
 * is. Which keeps the colours honest - a state nobody measured stays grey rather than being
 * painted amber for contrast.
 */
export const TraceChip = (props: ITraceChipProps) => (
    <span
        className={`trace-chip trace-chip--${props.tone ?? 'neutral'}`}
        title={props.hint}
    >
        {props.icon && (
            <FontAwesomeIcon icon={props.icon} className="trace-chip__icon" />
        )}

        {props.label && (
            <span className="trace-chip__label">{props.label}</span>
        )}

        {props.children != null && props.children !== '' && (
            <span
                className={
                    props.mono
                        ? 'trace-chip__value trace-chip__value--mono'
                        : 'trace-chip__value'
                }
            >
                {props.children}
            </span>
        )}
    </span>
);
